import { Viewer } from './viewer';

export type UserContext = 'logged_out' | 'client' | 'expert' | 'admin';

export interface UserContextOption {
  name: string;
  value: string;
  description?: string;
  billingAccountId?: string;
}

export function getUserContextOptions(
  user: any,
  userContext?: UserContext,
  groups?: any[]
): UserContextOption[] {
  let options: UserContextOption[] = [];

  options.push({
    value: 'client',
    name: user.first_name,
    billingAccountId: '',
  });

  if (user.signup_type === 'expert' || user.expert_state) {
    options.push({
      value: 'expert',
      name: user.first_name,
      description: 'Expert',
      billingAccountId: user.billing_account_id,
    });
  }

  if (user.admin) {
    options.push({
      value: 'admin',
      name: user.first_name,
      description: 'Admin',
      billingAccountId: user.billing_account_id,
    });
  }

  if (user.groups) {
    options = options.concat(
      user.groups.map((g: any) => ({
        value: g.id,
        name: g.name,
        billingAccountId: g.billing_account?.id || '',
      }))
    );
  }

  const group = userContext && groups && groups.find((g: any) => g.id === userContext);
  if (group && !options.some((o) => o.value === group.id)) {
    options.push({
      value: group.id,
      name: group.name,
      billingAccountId: group.billing_account?.id || '',
    });
  }

  return options;
}

export function isGroupContext(userContext: UserContext): boolean {
  return !!userContext && !['admin', 'client', 'expert', 'logged_out'].includes(userContext);
}

// A remembered "View As" context is only safe to replay if the viewer still holds it.
// A stale group id (membership revoked) would otherwise be replayed into a group:view
// query that gets denied — the ENG-5509 unhandled-rejection-on-load bug.
function isRememberedContextValid(currentContext: UserContext, viewer: Viewer): boolean {
  if (isGroupContext(currentContext)) {
    return !!viewer.groups?.some((g) => g.id === currentContext);
  }
  if (currentContext === 'admin') return !!viewer.admin;
  if (currentContext === 'expert')
    return !!(viewer.signup_type === 'expert' || viewer.expert_state);
  return true; // 'client' — always valid for a logged-in viewer
}

export function getDefaultUserContext(viewer: Viewer, currentContext?: UserContext): UserContext {
  if (!viewer.id) return 'logged_out';

  // A still-valid remembered context wins (preserves "View As"); a stale one falls
  // through to the default ladder below instead of being replayed. (ENG-5509)
  if (
    currentContext &&
    currentContext !== 'logged_out' &&
    isRememberedContextValid(currentContext, viewer)
  ) {
    return currentContext;
  }

  if (viewer.admin) return 'admin';

  if (viewer.groups && viewer.groups.length > 0) {
    // ENG-5256 product decision: for non-admin users, prefer the first group that
    // has a billing account so the dashboard defaults to the account context that
    // can show Account Overview (credits, stats, etc.). Falls back to groups[0]
    // when no group has a billing account (e.g. Viewer-only memberships).
    const billingGroupId = viewer.groups.find((g) => !!g.billing_account)?.id;
    return (billingGroupId ?? viewer.groups[0].id) as UserContext;
  }

  if (viewer.signup_type === 'expert' || viewer.expert_state) return 'expert';

  return 'client';
}

export function getUserOrGroup(userContext: UserContext, user: any): any {
  if (isGroupContext(userContext)) {
    return user.groups.find((g: any) => g.id === userContext);
  }
  return user;
}

/**
 * ENG-5351: resolve the group the viewer is currently operating as — the active
 * "view as" context (`ui.userContext`) when it is a group, otherwise the computed
 * default account group (`getDefaultUserContext`: first billing group, else groups[0]).
 * Returns undefined for personal / expert / admin-only contexts with no group, and
 * for a stale/non-member remembered group id (that case is cleared by ENG-5509).
 *
 * Callers must classify a multi-account user by the account they are acting in, not
 * by scanning all of `viewer.groups` — "any group is SMB" mis-gates multi-account users.
 */
export function resolveActiveContextGroup(
  viewer: Viewer,
  userContext: UserContext
): NonNullable<Viewer['groups']>[number] | undefined {
  const ctx = isGroupContext(userContext) ? userContext : getDefaultUserContext(viewer);
  if (!isGroupContext(ctx)) return undefined;
  return (viewer.groups ?? []).find((g) => g?.id === ctx);
}

/**
 * ENG-5506: resolve a team a booking was launched for (the network-invite notification's
 * "Request Expert" passes the team that owns the network). Only a membership with a
 * billing account qualifies; anything else returns undefined so callers keep the active
 * view-as account.
 */
export function resolveScopedBillingGroup(
  viewer: Viewer,
  groupId: string | null | undefined
): NonNullable<Viewer['groups']>[number] | undefined {
  if (!groupId) return undefined;
  const group = (viewer.groups ?? []).find((g) => g?.id === groupId);
  return group?.billing_account ? group : undefined;
}

export function hasAcceptedEmail(viewer: Viewer): boolean {
  return !!viewer.email?.accepted;
}
